import { useState, useRef, useCallback, useEffect, useMemo } from 'react'
import { useLineItemRow } from '@/hooks/use-line-item-row'
import { ItemPickerDropdown } from './item-picker-dropdown'
import { resolveRate } from '@/hooks/useMaterialCalculations'
import {
  calculateMaterialsFromWriteup,
  getFormulaKeysForSection,
  getMaterialBreakdown,
  getWriteupRowIdsForMaterial,
  WRITEUP_FIELD_DISPLAY,
  type MaterialBreakdownEntry,
} from '@/lib/material-formulas'
import { fmtUnit } from '@/lib/format-unit'
import type { PricingItem, LineItem, CreateLineItemPayload } from '@/types'

const DeleteIcon = () => (
  <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><path d="M15 9l-6 6M9 9l6 6"/></svg>
)

const DollarIcon = () => (
  <svg width="16" height="16" fill="none" stroke="#94A3B8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <line x1="12" x2="12" y1="2" y2="22"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
  </svg>
)

const InfoCircleIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="16" x2="12" y2="12" />
    <line x1="12" y1="8" x2="12.01" y2="8" />
  </svg>
)

const fmt = (v: number) => `$${v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
const fmtNum = (v: number) => v.toLocaleString('en-US', { maximumFractionDigits: 2 })
const selectAll = (e: React.MouseEvent<HTMLInputElement>) => (e.target as HTMLInputElement).select()

interface SectionConfig {
  section: string
  /** Light-gray suffix appended to material name to distinguish source (e.g. "R&R", "Overlay"). */
  suffix?: string
}

interface MaterialsSectionProps {
  lineItems: LineItem[]
  pricingItems: PricingItem[]
  section?: string
  /**
   * Render a single merged table whose rows are drawn from multiple sections in the order given.
   * When provided, this overrides `section` for row filtering, formula lookups, and pending previews.
   * Each row gets a light-gray suffix from its source SectionConfig.
   */
  sections?: SectionConfig[]
  serviceFilter?: string
  scopeData?: string
  label?: string
  /**
   * When provided, a "+ add material" picker is shown. Directly-added materials are
   * standalone line items (no pricingItemId): their cost counts toward the estimate,
   * they never appear as text on the proposal, and the write-up→materials sync never
   * touches them. The write-up path (BackgroundMaterialSync) is unaffected.
   */
  onAddItem?: (payload: CreateLineItemPayload) => Promise<void>
  onUpdateItem: (itemId: string, data: Partial<CreateLineItemPayload> & { lineTotal?: number }) => Promise<void>
  onDeleteItem: (itemId: string) => Promise<void>
  onWriteupRowDisable?: (writeupRowId: string) => void
}

export function MaterialsSection({ lineItems, pricingItems, section, sections, serviceFilter, scopeData, label, onAddItem, onUpdateItem, onDeleteItem, onWriteupRowDisable }: MaterialsSectionProps) {
  const sectionConfigs = useMemo<SectionConfig[]>(
    () => (sections && sections.length > 0 ? sections : [{ section: section ?? '' }]),
    [sections, section]
  )
  const sectionOrder = useMemo(() => {
    const m = new Map<string, number>()
    sectionConfigs.forEach((s, i) => m.set(s.section, i))
    return m
  }, [sectionConfigs])
  const suffixBySection = useMemo(() => {
    const m = new Map<string, string | undefined>()
    sectionConfigs.forEach((s) => m.set(s.section, s.suffix))
    return m
  }, [sectionConfigs])

  const rows = useMemo(() => {
    const matched = lineItems.filter(
      (li) => li.category === 'materials' && sectionOrder.has(li.section ?? '')
    )
    return [...matched].sort(
      (a, b) => (sectionOrder.get(a.section ?? '') ?? 0) - (sectionOrder.get(b.section ?? '') ?? 0)
    )
  }, [lineItems, sectionOrder])

  // Union of formulaKeys across all sections — used by delete handler to decide
  // whether the deleted line item maps back to a writeup row that should be unchecked.
  const formulaKeysBySection = useMemo(() => {
    const m = new Map<string, Set<string>>()
    for (const { section: s } of sectionConfigs) m.set(s, getFormulaKeysForSection(s))
    return m
  }, [sectionConfigs])

  // Build a lookup from pricingItemId → formulaKey for reverse mapping line items to formulas
  const pricingIdToFormulaKey = useMemo(() => {
    const map = new Map<string, string>()
    for (const p of pricingItems) {
      if (p.category === 'materials' && p.formulaKey) {
        map.set(p.id, p.formulaKey)
      }
    }
    return map
  }, [pricingItems])

  // Wrap delete to also uncheck writeup rows when removing a formula-driven material
  const handleDeleteMaterial = useCallback((li: LineItem) => {
    onDeleteItem(li.id)
    const fk = li.pricingItemId ? pricingIdToFormulaKey.get(li.pricingItemId) : undefined
    const liSection = li.section ?? ''
    const sectionFormulaKeys = formulaKeysBySection.get(liSection)
    if (onWriteupRowDisable && fk && sectionFormulaKeys?.has(fk)) {
      const writeupRowIds = getWriteupRowIdsForMaterial(fk, liSection)
      for (const rowId of writeupRowIds) {
        onWriteupRowDisable(rowId)
      }
    }
  }, [onDeleteItem, onWriteupRowDisable, formulaKeysBySection, pricingIdToFormulaKey])

  // Compute which formula-driven materials are expected but not yet in lineItems, per section.
  // Sync is handled exclusively by BackgroundMaterialSync — no sync runs here,
  // which prevents the race condition that occurred when both the active panel
  // and the background sync ran useMaterialCalculations for the same section.
  const materialPricingByFormulaKey = useMemo(() => {
    const map = new Map<string, PricingItem>()
    for (const p of pricingItems) {
      if (p.category === 'materials' && p.formulaKey) map.set(p.formulaKey, p)
    }
    return map
  }, [pricingItems])
  const existingPricingIdsBySection = useMemo(() => {
    const map = new Map<string, Set<string>>()
    for (const li of rows) {
      if (!li.pricingItemId) continue
      const key = li.section ?? ''
      const set = map.get(key) ?? new Set<string>()
      set.add(li.pricingItemId)
      map.set(key, set)
    }
    return map
  }, [rows])
  const pendingPreviews = useMemo(() => {
    const out: { materialName: string; quantity: number; unit: string; rate: number; total: number; section: string }[] = []
    for (const { section: s } of sectionConfigs) {
      const calculations = calculateMaterialsFromWriteup(scopeData, s)
      const aggregated = new Map<string, { formulaKey: string; quantity: number; unit: string }>()
      for (const calc of calculations) {
        const existing = aggregated.get(calc.formulaKey)
        if (existing) {
          aggregated.set(calc.formulaKey, { ...existing, quantity: Math.round((existing.quantity + calc.quantity) * 100) / 100 })
        } else {
          aggregated.set(calc.formulaKey, { ...calc })
        }
      }
      const existingIds = existingPricingIdsBySection.get(s) ?? new Set<string>()
      for (const calc of aggregated.values()) {
        const pricing = materialPricingByFormulaKey.get(calc.formulaKey)
        if (!pricing || existingIds.has(pricing.id)) continue
        const effectiveRate = resolveRate(pricing, serviceFilter)
        out.push({
          materialName: pricing.name,
          quantity: calc.quantity,
          unit: pricing.unit || calc.unit,
          rate: effectiveRate,
          total: calc.quantity * effectiveRate,
          section: s,
        })
      }
    }
    return out
  }, [sectionConfigs, scopeData, existingPricingIdsBySection, materialPricingByFormulaKey, serviceFilter])

  // Direct-add picker: materials the estimator adds by hand (in addition to the
  // ones the write-up auto-populates). Section a manual row lands in — the primary
  // section (for the merged asphalt table, that's 'asphalt').
  const targetSection = section ?? sectionConfigs[0]?.section ?? ''
  // Scope the picker to this tab's service so e.g. the Concrete tab doesn't list
  // materials assigned to Asphalt. A material with NO service is not tied to any
  // service, so keep it available on every tab — otherwise materials that ship
  // without a service (e.g. the default catalog) would be hidden everywhere and the
  // picker would be empty. Only materials assigned to a DIFFERENT service are hidden.
  const materialItems = useMemo(
    () => pricingItems.filter((p) => {
      if (p.category !== 'materials') return false
      if (!serviceFilter) return true
      if (!p.service) return true
      return p.service.split(',').map((s) => s.trim()).includes(serviceFilter)
    }),
    [pricingItems, serviceFilter]
  )
  const handleSelect = async (item: PricingItem) => {
    if (!onAddItem) return
    // Guard against silently double-counting: this material may already be on the
    // tab, either as an existing row or as one the write-up is about to auto-add
    // (pendingPreviews). Adding another standalone copy would double its cost, so
    // confirm first. The estimator can still intentionally add a second line.
    const alreadyPresent =
      rows.some((r) => r.description === item.name) ||
      pendingPreviews.some((p) => p.materialName === item.name)
    if (alreadyPresent) {
      const ok = window.confirm(
        `"${item.name}" is already on this tab (it may be coming from the write-up). Add another line anyway?`
      )
      if (!ok) return
    }
    await onAddItem({
      description: item.name,
      category: 'materials',
      quantity: 1,
      unit: item.unit || 'Each',
      rate: resolveRate(item, serviceFilter),
      section: targetSection,
      // Standalone: NO pricingItemId, so the write-up→materials sync
      // (keyed on pricingItem.formulaKey) never deletes or merges it, and deleting
      // it won't uncheck a write-up row. hideFromProposal keeps it cost-only even on
      // surfaces that list line items (e.g. striping).
      hideFromProposal: true,
    })
  }

  const handleFieldChange = async (li: LineItem, updates: Record<string, number>) => {
    // lineTotal from the row's full current field set (never a stale store field).
    const m = { ...li, ...updates }
    const lineTotal = (m.quantity || 0) * m.rate
    await onUpdateItem(li.id, { quantity: m.quantity, rate: m.rate, lineTotal })
  }

  // Per-row writeup breakdown — drives the "i" popover next to each material name.
  // Only formula-driven items (pricingItemId → formulaKey known) get a breakdown;
  // manually-added or legacy items return an empty array and render no icon.
  const breakdownByLineItemId = useMemo(() => {
    const map = new Map<string, MaterialBreakdownEntry[]>()
    for (const li of rows) {
      const fk = li.pricingItemId ? pricingIdToFormulaKey.get(li.pricingItemId) : undefined
      if (!fk) continue
      const entries = getMaterialBreakdown(scopeData, fk, li.section ?? '')
      if (entries.length > 0) map.set(li.id, entries)
    }
    return map
  }, [rows, pricingIdToFormulaKey, scopeData])

  const sectionTotal = rows.reduce((sum, li) => sum + li.quantity * li.rate, 0)
  const previewTotal = pendingPreviews.reduce((sum, p) => sum + p.total, 0)
  const hasContent = rows.length > 0 || pendingPreviews.length > 0

  return (
    <div className="sc-section">
      <div className="sc-section-header">
        <DollarIcon />
        <span className="sc-section-title">{label ?? 'Construction Materials'}</span>
        {/* Manually add a material. Write-up formulas still auto-populate this table
            (and carry their text to the proposal); a manually-added material adds only
            its cost — no write-up text on the proposal. */}
        {onAddItem && (
          <div className="sc-section-actions" onClick={(e) => e.stopPropagation()}>
            <ItemPickerDropdown items={materialItems} onSelect={handleSelect} serviceFilter={serviceFilter} />
          </div>
        )}
      </div>

      <div className="sc-table-wrap">
        <table className="sc-table">
          <thead>
            <tr>
              <th>Material</th>
              <th>Qty</th>
              <th>Unit</th>
              <th>Cost/Unit</th>
              <th className="col-right">Total</th>
              <th className="col-action"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((li) => (
              <SimpleRow
                key={li.id}
                item={li}
                suffix={suffixBySection.get(li.section ?? '')}
                breakdown={breakdownByLineItemId.get(li.id)}
                onFieldChange={handleFieldChange}
                onDelete={() => handleDeleteMaterial(li)}
                onRename={(id, description) => onUpdateItem(id, { description })}
              />
            ))}
            {pendingPreviews.map((preview) => (
              <tr key={`preview-${preview.section}-${preview.materialName}`} className="sc-shimmer-row">
                <td className="col-name"><span className="sc-shimmer-bar bar-name" /></td>
                <td><span className="sc-shimmer-bar bar-qty" /></td>
                <td><span className="sc-shimmer-bar bar-unit" /></td>
                <td><span className="sc-shimmer-bar bar-rate" /></td>
                <td className="col-right"><span className="sc-shimmer-bar bar-total" /></td>
                <td className="col-action"></td>
              </tr>
            ))}
          </tbody>
          {hasContent && (
            <tfoot>
              <tr>
                <td colSpan={4} className="col-right"><strong>Total</strong></td>
                <td className="col-right"><span className="sc-foot-total">{fmt(sectionTotal + previewTotal)}</span></td>
                <td></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  )
}

function SimpleRow({
  item, suffix, breakdown, onFieldChange, onDelete, onRename,
}: {
  item: LineItem
  suffix?: string
  breakdown?: MaterialBreakdownEntry[]
  onFieldChange: (li: LineItem, updates: Record<string, number>) => Promise<void>
  onDelete: () => void
  onRename: (itemId: string, description: string) => Promise<void>
}) {
  const [rateEditing, setRateEditing] = useState(false)
  const [editingName, setEditingName] = useState(false)
  const [nameValue, setNameValue] = useState(item.description)

  const { values, setField, onFocus, onBlur } = useLineItemRow(
    item,
    (it) => ({ quantity: it.quantity, rate: it.rate }),
    (v) => { onFieldChange(item, v) },
  )
  const qty = values.quantity
  const rate = values.rate

  return (
    <tr>
      <td className="col-name">
        {editingName ? (
          <input
            className="sc-input"
            style={{ minWidth: '120px' }}
            value={nameValue}
            autoFocus
            onChange={(e) => setNameValue(e.target.value)}
            onBlur={() => { setEditingName(false); onRename(item.id, nameValue) }}
            onKeyDown={(e) => { if (e.key === 'Enter') { setEditingName(false); onRename(item.id, nameValue) } if (e.key === 'Escape') { setEditingName(false); setNameValue(item.description) } }}
          />
        ) : (
          <>
            <span className="sc-input-rename" title="Click to rename" onClick={() => setEditingName(true)}>{nameValue}</span>
            {suffix && <span className="sc-source-suffix"> ({suffix})</span>}
            {breakdown && breakdown.length > 0 && <BreakdownPopover breakdown={breakdown} />}
          </>
        )}
      </td>
      <td><input type="number" className="sc-input" value={qty || ''}
        onChange={(e) => setField('quantity', parseFloat(e.target.value) || 0)}
        onFocus={onFocus} onBlur={onBlur} onDoubleClick={selectAll} min={0} /></td>
      <td>{fmtUnit(item.unit)}</td>
      <td><input type={rateEditing ? 'number' : 'text'} className="sc-input"
        value={rateEditing ? (rate || '') : ('$' + rate.toFixed(2))}
        onChange={(e) => setField('rate', parseFloat(e.target.value) || 0)}
        onFocus={() => { setRateEditing(true); onFocus() }}
        onBlur={() => { setRateEditing(false); onBlur() }} onDoubleClick={selectAll} min={0} /></td>
      <td className="col-right"><strong>{fmt(qty * rate)}</strong></td>
      <td className="col-action">
        <button onClick={onDelete} className="sc-icon-btn" title="Delete"><DeleteIcon /></button>
      </td>
    </tr>
  )
}

/**
 * Hover popover that explains where a material line item's quantity comes from.
 * Shown next to the material name when the row is formula-driven and at least
 * one enabled writeup row contributes to it. Hovering the (i) icon reveals one
 * entry per source writeup row: scope label, input fields (area/depth/etc.),
 * and the resulting quantity contribution.
 */
function BreakdownPopover({ breakdown }: { breakdown: MaterialBreakdownEntry[] }) {
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null)
  const iconRef = useRef<HTMLSpanElement>(null)

  // Position the tip ABOVE the icon, horizontally centered on it. Using fixed
  // coordinates lets the tip escape the table's overflow:auto scroll container,
  // which would otherwise clip the popover at the table boundaries.
  const open = useCallback(() => {
    const r = iconRef.current?.getBoundingClientRect()
    if (!r) return
    setPos({ top: r.top - 8, left: r.left + r.width / 2 })
  }, [])
  const close = useCallback(() => setPos(null), [])

  // Close on scroll/resize — fixed positioning would otherwise leave the tip
  // floating where the icon used to be.
  useEffect(() => {
    if (!pos) return
    window.addEventListener('scroll', close, true)
    window.addEventListener('resize', close)
    return () => {
      window.removeEventListener('scroll', close, true)
      window.removeEventListener('resize', close)
    }
  }, [pos, close])

  return (
    <span
      className="sc-bd-wrap"
      onMouseEnter={open}
      onMouseLeave={close}
      onClick={(e) => e.stopPropagation()}
    >
      <span ref={iconRef} className="sc-bd-icon" aria-label="Source breakdown"><InfoCircleIcon /></span>
      {pos && (
        <div className="sc-bd-tip" role="tooltip" style={{ top: pos.top, left: pos.left }}>
          {breakdown.map((entry, i) => (
            <div key={entry.rowId + '|' + i} className="sc-bd-entry">
              {entry.fields.map((f) => {
                const def = WRITEUP_FIELD_DISPLAY[f.key]
                const label = def?.label ?? f.key
                const unit = def?.unit ?? ''
                const valueStr = typeof f.value === 'number' ? fmtNum(f.value) : String(f.value)
                return (
                  <div key={f.key} className="sc-bd-field">
                    <span className="sc-bd-field-label">{label}:</span>{' '}
                    <span className="sc-bd-field-value">{valueStr}{unit ? ` ${unit}` : ''}</span>
                  </div>
                )
              })}
            </div>
          ))}
        </div>
      )}
    </span>
  )
}
